import { useEffect, useState } from "react";
import { setAccountOpeningStep } from "../../redux/accountOpening";
import "./OtpDialog.css";
import AuthCode from "react-auth-code-input";
import useOtpResendCountdown from "../../customHooks/useOtpResendCountdown";
import CustomModal from "../Modal/CustomModal";
import LivePicture from "../LivePicture";
import apiHooks from "../../customHooks/apis";
import { accountOpeningSteps } from "../../utils/constants";
import useLivenessCheck from "../../customHooks/useLivenessCheck";
import Loader from "../Loader";
import BvnValidationErrorPopup from "../BvnValidationErrorPopup";
import OtherOtpOptions from "./OtherOtpOptions";
import {
  IValidateOtpPayload,
  RequestOtpPayload,
} from "../../models/IEnums.model";
import PopUp from "../Popup";
import { copyText } from "../../utils/helpers";
import DocUpload from "../DocUpload";
import useProcessFile from "../../customHooks/useProcessFile";
import { useFormik } from "formik";
import InputErrorMessage from "../InputErrorMessage";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";

interface OtpDialogProps {
  hideFacial?: boolean;
  currentStep?: number;
  setCurrentStep?: (step: number) => void;
}
function OtpDialog({ currentStep, setCurrentStep }: OtpDialogProps) {
  const dispatch = useAppDispatch();
  const { processFile } = useProcessFile();
  const { countdownStr, resetTimer, countdownStop } = useOtpResendCountdown();
  const {
    useValidateOtp,
    useRequestOtp,
    useBVNFaceMatch,
    useRequestVoiceWhatsappOtp,
    useUploadFile,
  } = apiHooks;
  const { handleUploadFile, uploadingFile } = useUploadFile();
  const { handleValidateOtp, isValidatingOtp, goToNextStep } = useValidateOtp();
  const { handleRequestOtp, requestingOtp, userDetails } = useRequestOtp();
  const {
    handleRequestOtp: requestVoiceWhatsapp,
    requestingOtp: requesting,
    successful,
    setSuccessful,
  } = useRequestVoiceWhatsappOtp();
  const { handleFaceMatch, matchingFace } = useBVNFaceMatch();
  const {
    startLivenessCheck,
    openModal,
    setOpenModal,
    loggingLiveness,
    uploadedImageUrl,
  } = useLivenessCheck();
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(false);
  const [openOtherOptions, setOpenOtherOptions] = useState<boolean>(false);
  const [openUssd, setOpenUssd] = useState<boolean>(false);
  const [openFacialOptions, setOpenFacialOptions] = useState<boolean>(false);
  const [uploadImage, setUploadImage] = useState<boolean>(false);
  const { tierType } = useAppSelector((values) => values.stepper.data);
  const { handleSubmit, setValues, setFieldValue, values, errors } =
    useFormik<IValidateOtpPayload>({
      initialValues: {
        phoneNumber: "",
        bvn: "",
        firstName: "",
        lastName: "",
        middleName: "",
        otp: "",
      },
      onSubmit(values) {
        handleValidateOtp(
          values,
          !!tierType ? runAfterOtpValidation : undefined,
        );
      },
    });

  const runAfterOtpValidation = () => {
    if (tierType === 3) {
      return startLivenessCheck();
    }
    setCurrentStep?.(currentStep + 1);
  };

  const validating = isValidatingOtp || requestingOtp || requesting;

  const resendOtpPayload: RequestOtpPayload = {
    phoneNumber: userDetails?.phoneNumber || userDetails?.mobile,
    customerName:
      `${userDetails?.firstName || ""} ${userDetails?.lastName || ""}`.trim(),
    bvn: userDetails?.bvn || userDetails?.nin,
  };

  const resendOtp = () => {
    setFieldValue("otp", "");
    resendOtpPayload.emailAddress = userDetails?.email || "";
    return handleRequestOtp(resendOtpPayload, resetTimer);
  };

  const handleValidateFaceMatch = (file: File) => {
    const formData = new FormData();
    formData.append("Bvn", userDetails?.bvn || "");
    formData.append("NIN", userDetails?.nin || "");
    formData.append("DateOfBirth", userDetails?.dateOfBirth || "");
    formData.append("IdFaceFile", file);
    handleFaceMatch(formData, () =>
      processFile(file, (postedFilebase64) =>
        handleUploadFile(
          {
            postedFilebase64,
            documentName: file.name,
            filetype: file.type,
          },
          (url) => goToNextStep("bvn", url),
        ),
      ),
    );
  };

  const handleCloseModal = () => {
    openModal && setOpenModal(false);
    showDisclaimer && setShowDisclaimer(false);
    openOtherOptions && setOpenOtherOptions(false);
    successful && setSuccessful(false);
    openUssd && setOpenUssd(false);
    openFacialOptions && setOpenFacialOptions(false);
    uploadImage && setUploadImage(false);
  };

  const goBack = () => {
    if (setCurrentStep) return setCurrentStep(currentStep - 1);
    dispatch(setAccountOpeningStep(accountOpeningSteps.bvnNinValidation));
  };

  const handleClickBtns = (text: string) => {
    if (text === "Cancel" || text === "Close") return handleCloseModal();
    if (text === "ussd") return (setOpenUssd(true), setOpenOtherOptions(false));
    if (text === "face-match")
      return (setShowDisclaimer(true), setOpenOtherOptions(false));
    if (text.includes("Copy")) return copyText("*822*22#");
    if (text.includes("Liveness"))
      return (startLivenessCheck(), handleCloseModal());
    if (text.includes("Upload")) return setUploadImage(true);
    if (text === "whatsapp" || text === "call")
      return (
        requestVoiceWhatsapp(
          {
            ...resendOtpPayload,
            voiceOtp: text === "call",
            whatsapOtp: text === "whatsapp",
          },
          () => setOpenOtherOptions(true),
        ),
        handleCloseModal()
      );
    setShowDisclaimer(false);
    startLivenessCheck();
  };

  useEffect(() => {
    localStorage.removeItem("validationType");
  }, []);

  useEffect(() => {
    uploadedImageUrl && goToNextStep("bvn", uploadedImageUrl);
  }, [uploadedImageUrl]);

  useEffect(() => {
    setValues({
      ...values,
      phoneNumber: userDetails?.phoneNumber || userDetails?.mobile,
      firstName: userDetails?.firstName,
      lastName: userDetails?.lastName,
      middleName: userDetails?.middleName,
      bvn: userDetails?.nin || userDetails?.bvn,
    });
  }, [userDetails]);

  if (loggingLiveness) return <Loader />;
  return (
    <div>
      <div className=" card-form pl-5 pr-5 ">
        <form className="card-body text-center" onSubmit={handleSubmit}>
          <h4 className="card-title text-center pt-4 mt-5">Enter OTP</h4>
          <p className="text-muted">
            An OTP has been sent to the mobile number and email captured in{" "}
            <br />
            your BVN. Kindly enter the OTP to proceed.
          </p>

          <div className="mt-4 mb-4">
            <AuthCode
              containerClassName="d-flex gap-2 justify-content-center"
              inputClassName="text-center"
              placeholder="*"
              length={6}
              allowedCharacters="numeric"
              onChange={(res) => setFieldValue("otp", res)}
            />
            <InputErrorMessage errorMessage={errors.otp} />
          </div>

          <div className="d-flex align-items-center gap-2 mb-4 justify-content-center">
            <button
              className="btn btn-filled-white btn-outline-gold"
              onClick={goBack}
              type="button"
            >
              Previous
            </button>
            {validating ? (
              <div className="spinner-border text-gold" role="status">
                <span className="sr-only"></span>
              </div>
            ) : (
              <button
                disabled={values.otp.length != 6}
                type="submit"
                className="btn btn-filled-red proceed-btn"
              >
                Proceed
              </button>
            )}
          </div>
          {!validating && (
            <p className="d-flex flex-column">
              {countdownStop ? (
                <small>
                  Did not get the OTP?{" "}
                  <span className="font-weight-bold">
                    <u className="cursor-pointer" onClick={resendOtp}>
                      Resend OTP
                    </u>{" "}
                  </span>
                </small>
              ) : (
                <>
                  <small>OTP expires in {countdownStr}</small>
                  <p>
                    Didn’t get the OTP?{" "}
                    <u
                      onClick={() => setOpenOtherOptions(true)}
                      className="cursor-pointer"
                    >
                      Try Other Options
                    </u>
                  </p>
                </>
              )}
            </p>
          )}
        </form>
      </div>
      <CustomModal
        show={
          openModal ||
          showDisclaimer ||
          openOtherOptions ||
          openUssd ||
          openFacialOptions ||
          uploadImage
        }
        modalTitle={
          showDisclaimer || openFacialOptions
            ? ""
            : openOtherOptions
              ? "Other OTP Options"
              : openUssd
                ? "Dial USSD Code in your phone"
                : uploadImage
                  ? "Upload an image"
                  : "Take live picture"
        }
        onClickClose={successful || matchingFace ? undefined : handleCloseModal}
      >
        {openUssd && (
          <PopUp
            onClickBtn={handleClickBtns}
            message="*822*22#"
            cancelText="Close"
            btnText="Copy to Clipboard"
          />
        )}
        {openOtherOptions && (
          <OtherOtpOptions onClick={handleClickBtns} successful={successful} />
        )}
        {showDisclaimer && (
          <BvnValidationErrorPopup
            btnTexts={["Cancel", "Proceed"]}
            onClickBtn={handleClickBtns}
            redText="Please keep your eyes open during the scan so we can capture your biometric details correctly; it only takes a second."
            message={`To help keep your account safe, we’ll need to scan your face to confirm your identity.`}
            bottomMessage="Note: If you prefer to skip OTP verification, we’ll use the phone number linked to your BVN to open your altbank account."
          />
        )}
        <DocUpload
          matchingFace={matchingFace}
          isFaceMatch
          handleBvnFaceMatch={handleValidateFaceMatch}
          show={uploadImage}
        />
        {openFacialOptions && (
          <BvnValidationErrorPopup
            btnTexts={["Upload Image", "Liveness Check"]}
            onClickBtn={handleClickBtns}
          />
        )}
        {openModal && (
          <LivePicture
            btnText={uploadingFile ? "Upload" : ""}
            isValidating={matchingFace || uploadingFile}
            onClickValidate={handleValidateFaceMatch}
          />
        )}
      </CustomModal>
    </div>
  );
}

export default OtpDialog;
